import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

interface EditNoteDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (note: string) => Promise<boolean | void>;
  currentNote?: string;
  companyName?: string;
  isLoading?: boolean;
  error?: string | null;
}

const EditNoteDialog = ({
  open,
  onClose,
  onSave,
  currentNote = '',
  companyName = '',
  isLoading = false,
  error = null,
}: EditNoteDialogProps) => {
  const [content, setContent] = useState('');
  const textFieldRef = useRef<HTMLInputElement>(null);

  // Reset form when dialog opens/closes or when currentNote changes (e.g., after saving)
  useEffect(() => {
    if (open) {
      setContent(currentNote);
      // Position cursor at the end of the text when dialog opens or note updates
      setTimeout(() => {
        if (textFieldRef.current) {
          textFieldRef.current.focus();
          // For multiline TextField, we need to set the cursor position differently
          const textarea = textFieldRef.current.querySelector('textarea');
          if (textarea && currentNote) {
            const { length } = currentNote;
            textarea.setSelectionRange(length, length);
            textarea.scrollTop = textarea.scrollHeight;
          }
        }
      }, 200); // Increased delay to ensure the dialog is fully rendered
    } else {
      setContent('');
    }
  }, [open, currentNote]);

  const handleSave = async () => {
    if (!content.trim()) {
      return;
    }

    const trimmedNote = content.trim();
    const shouldKeepOpen = await onSave(trimmedNote);
    // Only close if explicitly told to (false) or if undefined (default behavior for backward compatibility)
    // If true is returned, keep the dialog open
    if (shouldKeepOpen !== true) {
      onClose();
    }
  };

  const handleCancel = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const isNoteEmpty = !content.trim();
  const isNoteChanged = content.trim() !== currentNote;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isLoading}
      onClick={e => e.stopPropagation()}
    >
      <DialogTitle onClick={e => e.stopPropagation()}>
        {currentNote && currentNote !== '-' ? 'Edit Note' : 'Add Note'}
        {companyName && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {companyName}
          </Typography>
        )}
      </DialogTitle>
      
      <DialogContent onClick={e => e.stopPropagation()}>
        <Box sx={{ pt: 1 }}>
          <TextField
            ref={textFieldRef}
            autoFocus
            multiline
            rows={4}
            fullWidth
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder="Enter your note here..."
            variant="outlined"
            disabled={isLoading}
            error={!!error}
            helperText={error}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }} onClick={e => e.stopPropagation()}>
        <Button
          onClick={handleCancel}
          disabled={isLoading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={isLoading || isNoteEmpty || !isNoteChanged}
          variant="contained"
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditNoteDialog;
